import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Search } from "lucide-react";
import { Input } from "@/shared/ui/Input";
import { Pagination } from "@/shared/ui/Pagination";
import { MobileSort, type SortOption } from "@/shared/ui/MobileSort";
import { useDebounce } from "@/shared/lib/useDebounce";
import { CustomerDrawer } from "@/features/customers/CustomerDrawer";
import { useRenewals } from "./hooks";
import { RenewalsTable } from "./RenewalsTable";
import {
  DAY_OPTIONS,
  PAGE_SIZE,
  type RenewalKind,
  type RenewalOrdering,
  type RenewalSortField,
  type RenewalStatus,
} from "./types";

const KINDS: RenewalKind[] = ["all", "free", "paid"];

// Until the operator clicks a column header the list follows the segment: expiring = soonest
// first, expired = most recently ended first.
function defaultOrdering(status: RenewalStatus): RenewalOrdering {
  return status === "expiring" ? "end_date" : "-end_date";
}

export function RenewalsScreen() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<RenewalStatus>("expiring");
  const [days, setDays] = useState<number>(14);
  const [kind, setKind] = useState<RenewalKind>("all");
  const [ordering, setOrdering] = useState<RenewalOrdering | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  const search = useDebounce(searchInput, 350);

  // Reset to page 1 whenever the query changes.
  useEffect(() => {
    setPage(1);
  }, [status, days, kind, ordering, search]);

  const effectiveOrdering = ordering ?? defaultOrdering(status);
  const { data, isLoading, isError, isFetching, refetch } = useRenewals({
    status,
    days,
    kind,
    search,
    ordering: effectiveOrdering,
    page,
  });

  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const rangeFrom = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeTo = Math.min(page * PAGE_SIZE, count);

  // Same fields/first directions as the RenewalsTable headers.
  const sortOptions: SortOption<RenewalSortField>[] = [
    { field: "end_date", label: t("renewals.colTerm"), firstDir: "asc" },
    { field: "user__first_name", label: t("renewals.colMerchant"), firstDir: "asc" },
    { field: "is_free", label: t("renewals.colPremium"), firstDir: "asc" },
    { field: "active_agreements", label: t("renewals.colAgreements") },
    { field: "accepted_clients_count", label: t("renewals.colClients") },
    { field: "last_login_at", label: t("renewals.colActivity") },
  ];

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-[20px] font-bold text-grey-900">{t("renewals.title")}</h1>
        <p className="mt-1 text-[13.5px] text-grey-500">{t("renewals.subtitle")}</p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="flex items-center gap-1.5" role="group" aria-label={t("renewals.title")}>
          {(["expiring", "expired"] as const).map((s) => (
            <FilterChip key={s} selected={status === s} onClick={() => setStatus(s)}>
              {t(s === "expiring" ? "renewals.statusExpiring" : "renewals.statusExpired")}
            </FilterChip>
          ))}
        </div>

        <div className="flex items-center gap-1.5" role="group" aria-label={t("renewals.daysLabel")}>
          {DAY_OPTIONS.map((d) => (
            <FilterChip key={d} selected={days === d} onClick={() => setDays(d)}>
              {t("renewals.daysOption", { n: d })}
            </FilterChip>
          ))}
        </div>

        <div className="flex items-center gap-1.5" role="group" aria-label={t("renewals.kindLabel")}>
          {KINDS.map((k) => (
            <FilterChip key={k} selected={kind === k} onClick={() => setKind(k)}>
              {t(`renewals.kind_${k}`)}
            </FilterChip>
          ))}
        </div>

        <div className="relative w-full sm:w-[260px]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey-400" />
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("renewals.searchPlaceholder")}
            aria-label={t("renewals.searchPlaceholder")}
            className="h-[38px] pl-9 pr-3"
          />
        </div>

        {/* Phones: the count shares a row with the sort picker; md:contents keeps the desktop row as-is. */}
        <div className="flex w-full items-center gap-3 md:contents">
          <span className="tnum flex-none text-[13.5px] font-medium text-grey-500">
            {t("renewals.total", { count })}
          </span>
          <MobileSort
            options={sortOptions}
            ordering={effectiveOrdering}
            onSortChange={setOrdering}
            className="ml-auto w-[210px] md:hidden"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-card border border-grey-200 bg-white shadow-card">
        {isError && !data ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <AlertTriangle size={32} className="mb-3 text-red-bright" strokeWidth={1.75} />
            <p className="text-[15px] font-semibold text-grey-800">{t("renewals.loadError")}</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-4 rounded-control border border-grey-200 px-4 py-2 text-[13px] font-medium text-grey-700 hover:bg-grey-100"
            >
              {t("renewals.retry")}
            </button>
          </div>
        ) : (
          <RenewalsTable
            rows={data?.results ?? []}
            loading={isLoading}
            ordering={effectiveOrdering}
            onSortChange={setOrdering}
            onOpen={setOpenId}
          />
        )}

        <div className="flex items-center justify-between gap-3 border-t border-grey-200 bg-grey-25 px-4 py-3">
          <span className="tnum min-w-0 text-[13px] text-grey-500">
            {t("renewals.rangeShown", { from: rangeFrom, to: rangeTo, total: count })}
          </span>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} isFetching={isFetching} />
        </div>
      </div>

      <CustomerDrawer customerId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function FilterChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`h-[38px] rounded-pill border px-3.5 text-[13px] font-semibold transition active:scale-95 ${
        selected
          ? "border-green bg-green text-white"
          : "border-grey-300 bg-white text-grey-700 hover:bg-grey-50"
      }`}
    >
      {children}
    </button>
  );
}
